import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * El almacén local del dispositivo.
 *
 * Guarda dos cosas que no se parecen en nada y por eso viven separadas:
 *
 * · `cola`   — altas que la persona cargó y el servidor todavía no confirmó.
 *              Es información que NO existe en ningún otro lado. Se borra sólo
 *              cuando el servidor dice que sí.
 *
 * · `cache`  — copia de lecturas que sí existen en el servidor, para poder
 *              abrir la aplicación sin señal. Es descartable: si se pierde, se
 *              vuelve a pedir.
 *
 * Que estén en la misma base pero en depósitos distintos importa: limpiar el
 * caché nunca puede llevarse por delante un trabajo sin sincronizar.
 */

/** Por dónde va un alta cargada en el dispositivo. */
export type EstadoEnvio =
  /** Cargada, esperando su turno. */
  | 'pendiente'
  /** Se está enviando ahora mismo. */
  | 'enviando'
  /** El servidor la aceptó. */
  | 'sincronizado'
  /** El servidor la rechazó, o no se pudo enviar. Se conserva y se reintenta. */
  | 'error';

/**
 * Lo justo para dibujar el trabajo sin volver a abrir el borrador.
 *
 * Creció cuando los trabajos pendientes pasaron a mostrarse DENTRO de la lista
 * del mes, mezclados con los demás, en vez de en un recuadro aparte arriba. Una
 * tarjeta que se ve distinta de las de al lado se lee como otra cosa; para
 * verse igual necesita lo mismo que muestran ellas.
 *
 * El importe es el que la persona vio al guardar. No se recalcula acá: el
 * número que quedó en la pantalla cuando apretó Guardar es el que tiene que
 * seguir viendo, y si el servidor después calcula otro, eso se discute con el
 * trabajo ya creado y no con el borrador.
 */
export interface ResumenAlta {
  cliente: string;
  /** El campo, si se eligió uno. Es el segundo renglón de la tarjeta. */
  establecimiento?: string | null;
  fecha: string;
  servicios: string;
  /** `null` = el período no tenía precio de referencia cargado. */
  totalCents?: number | null;
  /**
   * A qué mes se factura. Sin esto, un trabajo cargado sin señal para octubre
   * aparecería en septiembre sólo porque es el mes que está abierto en
   * pantalla, y ese es exactamente el tipo de error que después no se explica.
   */
  billingYear?: number;
  billingMonth?: number;
}

export interface AltaEnCola {
  /**
   * Identidad de la OPERACIÓN de alta. Nace acá, junto con el borrador, y no
   * cambia nunca: es lo que permite reintentar sin duplicar. Si cambiara entre
   * intentos, el servidor crearía dos trabajos.
   */
  clientRequestId: string;
  organizationId: string;
  /** El mismo cuerpo que recibe `create_work_session`, tal como se armó. */
  payload: unknown;
  estado: EstadoEnvio;
  /** Para mostrar algo reconocible en la lista sin tener que abrir el borrador. */
  resumen: ResumenAlta;
  creadoEn: number;
  intentos: number;
  ultimoIntento: number | null;
  /** Qué dijo el servidor la última vez que falló. En su idioma, sin adornos. */
  ultimoError: string | null;
  /** Id del trabajo ya creado, una vez que el servidor confirmó. */
  workSessionId: string | null;
}

interface Esquema extends DBSchema {
  cola: {
    key: string;
    value: AltaEnCola;
    indexes: { 'por-organizacion': string };
  };
  cache: {
    key: string;
    value: { clave: string; valor: unknown; guardadoEn: number };
  };
}

const NOMBRE = 'gestion-veterinaria';
const VERSION = 1;

let conexion: Promise<IDBPDatabase<Esquema>> | null = null;

export function abrirAlmacen(): Promise<IDBPDatabase<Esquema>> {
  conexion ??= openDB<Esquema>(NOMBRE, VERSION, {
    upgrade(db) {
      const cola = db.createObjectStore('cola', { keyPath: 'clientRequestId' });
      cola.createIndex('por-organizacion', 'organizationId');
      db.createObjectStore('cache', { keyPath: 'clave' });
    },
  });
  return conexion;
}

/**
 * Cierra la conexión.
 *
 * Hace falta porque una base con conexiones abiertas no se puede borrar ni
 * migrar: el pedido queda esperando para siempre. Lo usan las pruebas entre
 * casos, y serviría para cambiar de organización sin dejar la anterior abierta.
 */
export async function cerrarAlmacen(): Promise<void> {
  if (!conexion) return;
  const db = await conexion;
  db.close();
  conexion = null;
}

/**
 * ¿Se puede guardar algo en este dispositivo?
 *
 * En navegación privada, y en algunos navegadores viejos, IndexedDB existe pero
 * falla al abrirse. Conviene saberlo ANTES de dejar que alguien cargue un
 * trabajo sin señal, porque prometer que se guardó y que después no esté es
 * peor que avisar de entrada.
 */
export async function almacenDisponible(): Promise<boolean> {
  try {
    await abrirAlmacen();
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Cola
// ---------------------------------------------------------------------------

export async function guardarEnCola(alta: AltaEnCola): Promise<void> {
  const db = await abrirAlmacen();
  await db.put('cola', alta);
}

export async function leerCola(organizationId: string): Promise<AltaEnCola[]> {
  const db = await abrirAlmacen();
  const todas = await db.getAllFromIndex('cola', 'por-organizacion', organizationId);
  return todas.sort((a, b) => a.creadoEn - b.creadoEn);
}

export async function leerDeCola(clientRequestId: string): Promise<AltaEnCola | undefined> {
  const db = await abrirAlmacen();
  return db.get('cola', clientRequestId);
}

/**
 * Saca un alta de la cola. Se llama SÓLO con la confirmación del servidor en la
 * mano; mientras haya cualquier duda, el borrador se queda.
 */
export async function quitarDeCola(clientRequestId: string): Promise<void> {
  const db = await abrirAlmacen();
  await db.delete('cola', clientRequestId);
}

// ---------------------------------------------------------------------------
// Caché de lecturas
// ---------------------------------------------------------------------------

export async function guardarEnCache(clave: string, valor: unknown): Promise<void> {
  const db = await abrirAlmacen();
  await db.put('cache', { clave, valor, guardadoEn: Date.now() });
}

export async function leerDeCache<T>(
  clave: string,
): Promise<{ valor: T; guardadoEn: number } | null> {
  const db = await abrirAlmacen();
  const fila = await db.get('cache', clave);
  return fila ? { valor: fila.valor as T, guardadoEn: fila.guardadoEn } : null;
}

export async function borrarDeCache(clave: string): Promise<void> {
  const db = await abrirAlmacen();
  await db.delete('cache', clave);
}

/**
 * Vaciar TODO el caché de lecturas, y sólo ése.
 *
 * Lo usa `Salir`: los datos de la organización no tienen por qué seguir en un
 * teléfono donde ya nadie inició sesión. La cola queda intacta a propósito —son
 * trabajos cargados que no existen en ningún otro lado, y se sincronizan cuando
 * esa misma persona vuelva a entrar—. Que estén en depósitos separados es
 * justamente para poder hacer esto sin riesgo.
 */
export async function borrarCacheDeLecturas(): Promise<void> {
  try {
    const db = await abrirAlmacen();
    await db.clear('cache');
  } catch {
    // Si el almacén no abre no hay nada que limpiar, y no es motivo para que
    // cerrar sesión falle.
  }
}
